import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { DomainGroupPicker, PolicySchedulePicker } from "../../components/network/PolicyAssociationPickers";
import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { DateTimeField } from "../../components/ui/DateTimeField";
import { FormField } from "../../components/ui/FormField";
import { Glossary } from "../../components/ui/Glossary";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { MasterDetailLayout } from "../../components/ui/MasterDetailLayout";
import { RadioCardGroup } from "../../components/ui/RadioCard";
import { SearchInput } from "../../components/ui/SearchInput";
import { SegmentedFilter } from "../../components/ui/SegmentedFilter";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { Switch } from "../../components/ui/Switch";
import { type Column, Table } from "../../components/ui/Table";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import { appGroupsApi } from "../../lib/api/network/appGroups";
import { deviceGroupsApi } from "../../lib/api/network/deviceGroups";
import {
  type GroupPolicyCreateInput,
  type GroupPolicyDefaultAction,
  type GroupPolicyRow,
  type PolicyActivation,
  type UnknownDomainAction,
  groupPoliciesApi,
} from "../../lib/api/network/groupPolicies";
import { urlGroupsApi } from "../../lib/api/network/urlGroups";
import { formatDateTime } from "../../lib/format";
import { formatMessage, t } from "../../lib/i18n";
import { useRecompileNotice } from "../../lib/recompileNotice";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

type ActionFilter = "all" | "allow" | "block";
type ActiveFilter = "all" | "active" | "inactive";
type EditorMode = "create" | "edit";

interface EditorState {
  mode: EditorMode;
  rowId: string | null;
  name: string;
  deviceGroup: string;
  urlGroupIds: string[];
  /** Linked `AppGroup` ids. Persisted as `allowed_app_groups` on submit. */
  allowedAppGroupIds: string[];
  defaultAction: GroupPolicyDefaultAction;
  activation: PolicyActivation;
  unknownDomainAction: UnknownDomainAction;
  priority: number;
  isActive: boolean;
  validFrom: string;
  validUntil: string;
  scheduleProfileId: string;
  notificationsEnabled: boolean;
  activeNotificationTitle: string;
  activeNotificationBody: string;
  inactiveNotificationTitle: string;
  inactiveNotificationBody: string;
}

function makeBlankEditor(): EditorState {
  return {
    mode: "create",
    rowId: null,
    name: "",
    deviceGroup: "",
    urlGroupIds: [],
    allowedAppGroupIds: [],
    defaultAction: "block",
    activation: "always",
    unknownDomainAction: "log",
    priority: 50,
    isActive: true,
    validFrom: "",
    validUntil: "",
    scheduleProfileId: "",
    notificationsEnabled: true,
    activeNotificationTitle: "",
    activeNotificationBody: "",
    inactiveNotificationTitle: "",
    inactiveNotificationBody: "",
  };
}

function formatDateForInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function inputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.valueOf())) return null;
  return d.toISOString();
}

export function RulesListTab() {
  const qc = useQueryClient();
  const notice = useRecompileNotice();
  const { push: pushToast } = useToast();
  const [params, setParams] = useSearchParams();

  const [editor, setEditor] = useState<EditorState>(makeBlankEditor);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);

  const [detail, setDetail] = useState<GroupPolicyRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<GroupPolicyRow | null>(null);

  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");

  const [search, setSearch] = useState(params.get("q") ?? "");
  const [actionFilter, setActionFilter] = useState<ActionFilter>(
    (params.get("action") as ActionFilter) || "all",
  );
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>(
    (params.get("active") as ActiveFilter) || "all",
  );
  const filterDeviceGroup = params.get("deviceGroup") ?? "";
  const filterDestinationGroup = params.get("destinationGroup") ?? "";
  const filterSchedule = params.get("scheduleProfile") ?? "";
  const filterAllowedAppGroup = params.get("allowedAppGroup") ?? "";

  function updateParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (!v || v === "all" || v === "") next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  }

  useEffect(() => {
    updateParams({ q: search || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const list = useQuery({
    queryKey: ["rules", "list"],
    queryFn: groupPoliciesApi.list,
  });
  const deviceGroups = useQuery({
    queryKey: ["rules", "device-groups", "picker"],
    queryFn: deviceGroupsApi.list,
  });
  const urlGroups = useQuery({
    queryKey: ["rules", "url-groups", "picker"],
    queryFn: urlGroupsApi.list,
  });
  const appGroups = useQuery({
    queryKey: ["rules", "app-groups", "picker"],
    queryFn: appGroupsApi.list,
  });


  const customDeviceGroupCount =
    deviceGroups.data?.filter((group) => !group.is_default).length ?? 0;

  const createDeviceGroup = useMutation({
    mutationFn: () =>
      deviceGroupsApi.create({ name: newGroupName, description: newGroupDescription }),
    onSuccess: (group) => {
      void qc.invalidateQueries({ queryKey: ["rules", "device-groups", "picker"] });
      void qc.invalidateQueries({ queryKey: ["network", "device-groups"] });
      setEditor((e) => ({ ...e, deviceGroup: group.id }));
      setCreateGroupOpen(false);
      setNewGroupName("");
      setNewGroupDescription("");
    },
  });

  const create = useMutation({
    mutationFn: (body: GroupPolicyCreateInput) => groupPoliciesApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules", "list"] });
      setEditorOpen(false);
      setEditor(makeBlankEditor());
      notice();
    },
    onError: (err) => setEditorError(translateError(err)),
  });

  const editMut = useMutation({
    mutationFn: (vars: { id: string; body: Partial<GroupPolicyCreateInput> }) =>
      groupPoliciesApi.patch(vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules", "list"] });
      setEditorOpen(false);
      setEditor(makeBlankEditor());
      notice();
    },
    onError: (err) => setEditorError(translateError(err)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => groupPoliciesApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules", "list"] });
      setPendingDelete(null);
      notice();
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("errors.generic"), body: translateError(err) }),
  });

  const togglePriority = useMutation({
    mutationFn: (vars: { id: string; priority: number }) =>
      groupPoliciesApi.patch(vars.id, { priority: vars.priority }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules", "list"] });
      notice();
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("errors.generic"), body: translateError(err) }),
  });

  const toggleActive = useMutation({
    mutationFn: (vars: { id: string; is_active: boolean }) =>
      groupPoliciesApi.patch(vars.id, { is_active: vars.is_active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules", "list"] });
      notice();
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("errors.generic"), body: translateError(err) }),
  });

  function openCreate() {
    setEditor(makeBlankEditor());
    setEditorError(null);
    setEditorOpen(true);
  }

  function openEdit(row: GroupPolicyRow) {
    setEditor({
      mode: "edit",
      rowId: row.id,
      name: row.name,
      deviceGroup: row.device_group,
      urlGroupIds: row.url_group_ids,
      allowedAppGroupIds: row.allowed_app_group_ids ?? [],
      defaultAction: row.default_action,
      priority: row.priority,
      isActive: row.is_active,
      activation: row.activation,
      unknownDomainAction: row.unknown_domain_action,
      validFrom: formatDateForInput(row.valid_from),
      validUntil: formatDateForInput(row.valid_until),
      scheduleProfileId: row.schedule_profile_id ?? "",
      notificationsEnabled: row.notifications_enabled,
      activeNotificationTitle: row.active_notification_title,
      activeNotificationBody: row.active_notification_body,
      inactiveNotificationTitle: row.inactive_notification_title,
      inactiveNotificationBody: row.inactive_notification_body,
    });
    setEditorError(null);
    setEditorOpen(true);
  }

  function openClone(row: GroupPolicyRow) {
    setEditor({
      mode: "create",
      rowId: null,
      name: row.name + t("rules.cloneSuffix"),
      deviceGroup: row.device_group,
      urlGroupIds: [...row.url_group_ids],
      allowedAppGroupIds: [...(row.allowed_app_group_ids ?? [])],
      defaultAction: row.default_action,
      activation: row.activation,
      unknownDomainAction: row.unknown_domain_action,
      priority: row.priority + 10,
      isActive: row.is_active,
      validFrom: formatDateForInput(row.valid_from),
      validUntil: formatDateForInput(row.valid_until),
      scheduleProfileId: row.schedule_profile_id ?? "",
      notificationsEnabled: row.notifications_enabled,
      activeNotificationTitle: row.active_notification_title,
      activeNotificationBody: row.active_notification_body,
      inactiveNotificationTitle: row.inactive_notification_title,
      inactiveNotificationBody: row.inactive_notification_body,
    });
    setEditorError(null);
    setEditorOpen(true);
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setEditorError(null);
    if (!editor.name.trim()) {
      setEditorError(t("rules.editor.errors.nameRequired"));
      return;
    }
    if (!editor.deviceGroup) {
      setEditorError(t("rules.editor.errors.deviceGroupRequired"));
      return;
    }
    if (editor.defaultAction === "block" && editor.urlGroupIds.length === 0) {
      setEditorError(t("rules.editor.errors.blockWithoutDestinations"));
      return;
    }
    if (editor.activation === "schedule" && !editor.scheduleProfileId) {
      setEditorError(t("ruleWizard.activation.errorScheduleRequired"));
      return;
    }
    const payload: GroupPolicyCreateInput = {
      name: editor.name,
      device_group: editor.deviceGroup,
      url_groups: editor.urlGroupIds,
      // Always send the field — even an empty array — so an edit that
      // removes all linked app groups actually clears the M2M instead of
      // leaving the old value behind.
      allowed_app_groups: editor.allowedAppGroupIds,
      default_action: editor.defaultAction,
      activation: editor.activation,
      unknown_domain_action: editor.unknownDomainAction,
      priority: editor.priority,
      is_active: editor.isActive,
      valid_from: inputToIso(editor.validFrom),
      valid_until: inputToIso(editor.validUntil),
      schedule_profile_id:
        editor.activation === "always" ? null : editor.scheduleProfileId || null,
      notifications_enabled: editor.notificationsEnabled,
      active_notification_title: editor.activeNotificationTitle,
      active_notification_body: editor.activeNotificationBody,
      inactive_notification_title: editor.inactiveNotificationTitle,
      inactive_notification_body: editor.inactiveNotificationBody,
    };
    if (editor.mode === "edit" && editor.rowId) {
      editMut.mutate({ id: editor.rowId, body: payload });
    } else {
      create.mutate(payload);
    }
  }

  function toggleAppGroupSelection(id: string) {
    setEditor((e) => ({
      ...e,
      allowedAppGroupIds: e.allowedAppGroupIds.includes(id)
        ? e.allowedAppGroupIds.filter((g) => g !== id)
        : [...e.allowedAppGroupIds, id],
    }));
  }

  const filteredRows = useMemo(() => {
    if (!list.data) return [];
    const q = search.trim().toLowerCase();
    return list.data
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .filter((row) => {
        if (q && !row.name.toLowerCase().includes(q) && !row.device_group_name.toLowerCase().includes(q) && !(row.url_group_names ?? []).some((name) => name.toLowerCase().includes(q)) && !(row.schedule_profile_name ?? "").toLowerCase().includes(q)) {
          return false;
        }
        if (filterSchedule && row.schedule_profile_id !== filterSchedule) return false;
        if (actionFilter !== "all" && row.default_action !== actionFilter) return false;
        if (activeFilter === "active" && !row.is_active) return false;
        if (activeFilter === "inactive" && row.is_active) return false;
        if (filterDeviceGroup && row.device_group !== filterDeviceGroup) return false;
        if (
          filterDestinationGroup &&
          !row.url_group_ids.includes(filterDestinationGroup)
        ) {
          return false;
        }
        if (
          filterAllowedAppGroup &&
          !(row.allowed_app_group_ids ?? []).includes(filterAllowedAppGroup)
        ) {
          return false;
        }
        return true;
      });
  }, [
    list.data,
    search,
    actionFilter,
    activeFilter,
    filterDeviceGroup,
    filterDestinationGroup,
    filterAllowedAppGroup,
    filterSchedule,
  ]);

  const sortedRows = filteredRows;

  function moveRule(row: GroupPolicyRow, direction: "up" | "down") {
    const idx = sortedRows.findIndex((r) => r.id === row.id);
    if (idx < 0) return;
    const target = direction === "up" ? sortedRows[idx - 1] : sortedRows[idx + 1];
    if (!target) return;
    const a = togglePriority.mutateAsync({ id: row.id, priority: target.priority });
    const b = togglePriority.mutateAsync({ id: target.id, priority: row.priority });
    void Promise.allSettled([a, b]);
  }

  const columns: Column<GroupPolicyRow>[] = [
    {
      key: "name",
      header: t("rules.column.name"),
      render: (row) => (
        <button
          type="button"
          onClick={() => setDetail(row)}
          className="app-name"
          style={{
            background: "none",
            border: 0,
            cursor: "pointer",
            padding: 0,
            color: "var(--accent)",
          }}
        >
          {row.name}
        </button>
      ),
    },
    {
      key: "who",
      header: t("rules.column.who"),
      render: (row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span className="app-name">{row.device_group_name}</span>
          <span className="app-pkg">
            {row.device_count} {t("rules.devicesLabel")}
          </span>
        </div>
      ),
    },
    {
      key: "what",
      header: t("rules.column.what"),
      render: (row) =>
        row.url_group_names && row.url_group_names.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>
              {row.url_group_names.join(" · ")}
            </span>
            <span className="app-pkg">
              {row.destination_count} {t("rules.destinationsLabel")}
            </span>
          </div>
        ) : (
          <span className="badge">{t("rules.column.whatEmpty")}</span>
        ),
    },
    {
      key: "allowed_apps",
      header: t("rules.column.allowedApps"),
      render: (row) =>
        (row.allowed_app_group_names ?? []).length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 13.5 }}>
              {row.allowed_app_group_names.join(" · ")}
            </span>
            <span className="app-pkg">
              {row.allowed_apps_count} {t("rules.allowedAppsLabel")}
            </span>
          </div>
        ) : (
          <span className="app-pkg">—</span>
        ),
    },
    {
      key: "activation",
      header: t("rules.column.activation"),
      render: (row) => (
        <span className="badge">{t(`policyActivation.${row.activation}`)}</span>
      ),
    },
    {
      key: "schedule",
      header: t("rules.column.scheduleProfile"),
      render: (row) =>
        row.schedule_profile_name ? (
          <Link to={`/policies/schedules?id=${encodeURIComponent(row.schedule_profile_id ?? "")}`}>{row.schedule_profile_name}</Link>
        ) : (
          <span className="app-pkg">—</span>
        ),
    },
    {
      key: "action",
      header: t("rules.column.action"),
      render: (row) =>
        row.default_action === "allow" ? (
          <span className="badge badge--danger">{t("rules.action.blockListed")}</span>
        ) : (
          <span className="badge badge--success">{t("rules.action.allowListed")}</span>
        ),
    },
    {
      key: "priority",
      header: t("rules.column.priority"),
      render: (row) => {
        const idx = sortedRows.findIndex((r) => r.id === row.id);
        return (
          <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
            <span className="num">{row.priority}</span>
            <Button
              variant="ghost"
              size="sm"
              disabled={idx === 0 || togglePriority.isPending}
              onClick={(e) => {
                e.stopPropagation();
                moveRule(row, "up");
              }}
              aria-label={t("rules.action_move.up")}
            >
              ↑
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={idx === sortedRows.length - 1 || togglePriority.isPending}
              onClick={(e) => {
                e.stopPropagation();
                moveRule(row, "down");
              }}
              aria-label={t("rules.action_move.down")}
            >
              ↓
            </Button>
          </div>
        );
      },
      align: "right",
    },
    {
      key: "status",
      header: t("rules.column.status"),
      render: (row) => (
        <Switch
          checked={row.is_active}
          disabled={toggleActive.isPending}
          onChange={(next) => toggleActive.mutate({ id: row.id, is_active: next })}
        />
      ),
    },
    {
      key: "updated",
      header: t("rules.column.updated"),
      render: (row) => <span className="mono">{formatDateTime(row.updated_at)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div style={{ display: "inline-flex", gap: 4 }}>
          <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
            {t("rules.action_edit")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openClone(row)}>
            {t("rules.action_clone")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPendingDelete(row)}
          >
            {t("rules.action_delete")}
          </Button>
        </div>
      ),
    },
  ];

  const editorTitle =
    editor.mode === "edit" ? t("rules.editor.titleEdit") : t("rules.editor.title");
  const submitLabel =
    editor.mode === "edit" ? t("rules.editor.submitEdit") : t("rules.editor.submit");

  const activeFilterChip =
    filterDeviceGroup || filterDestinationGroup || filterAllowedAppGroup || filterSchedule ? (
      <Alert tone="info">
        {filterSchedule && <span>{t("rules.column.scheduleProfile")}: {filterSchedule} </span>}
        {filterDeviceGroup && (
          <>
            {t("rules.column.who")}:{" "}
            <strong>
              {deviceGroups.data?.find((g) => g.id === filterDeviceGroup)?.name ??
                filterDeviceGroup}
            </strong>{" "}
          </>
        )}
        {filterDestinationGroup && (
          <>
            {t("rules.column.what")}:{" "}
            <strong>
              {urlGroups.data?.find((g) => g.id === filterDestinationGroup)?.name ??
                filterDestinationGroup}
            </strong>{" "}
          </>
        )}
        {filterAllowedAppGroup && (
          <>
            {t("rules.column.allowedApps")}:{" "}
            <strong>
              {appGroups.data?.find((g) => g.id === filterAllowedAppGroup)?.name ??
                filterAllowedAppGroup}
            </strong>{" "}
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            updateParams({
              deviceGroup: null,
              destinationGroup: null,
              allowedAppGroup: null,
              scheduleProfile: null,
            })
          }
        >
          {t("common.bulk.clear")}
        </Button>
      </Alert>
    ) : null;

  return (
    <>
      <MasterDetailLayout
        ariaLabel={editorTitle}
        onClose={() => setEditorOpen(false)}
        list={<>
      <div
        className="filter-bar"
        style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}
      >
        <SearchInput
          placeholder={t("rules.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <SegmentedFilter<ActionFilter>
          value={actionFilter}
          onChange={(v) => {
            setActionFilter(v);
            updateParams({ action: v });
          }}
          options={[
            { value: "all", label: t("rules.filter.all") },
            { value: "block", label: t("rules.action.allowListed"), dot: "allow" },
            { value: "allow", label: t("rules.action.blockListed"), dot: "block" },
          ]}
        />
        <SegmentedFilter<ActiveFilter>
          value={activeFilter}
          onChange={(v) => {
            setActiveFilter(v);
            updateParams({ active: v });
          }}
          options={[
            { value: "all", label: t("rules.filter.all") },
            { value: "active", label: t("rules.filter.activeOnly"), dot: "allow" },
            { value: "inactive", label: t("rules.filter.inactiveOnly"), dot: "unknown" },
          ]}
        />
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Link to="/rules/new">
            <Button variant="secondary">{t("rules.wizardRule")}</Button>
          </Link>
          <Button onClick={openCreate}>{t("rules.newRule")}</Button>
        </div>
      </div>

      {activeFilterChip}

      <Card padding="none">
        {list.isLoading && <LoadingState />}
        {list.error && (
          <ErrorState titleKey="states.errorTitle" message={translateError(list.error)} />
        )}
        {list.data && (
          <Table
            rows={sortedRows}
            columns={columns}
            rowKey={(row) => row.id}
            empty={<EmptyState titleKey="rules.emptyTitle" bodyKey="rules.emptyBody" />}
          />
        )}
      </Card>
        </>}
        detail={editorOpen ? (
          <>
            <div className="master-detail__panel-header">
              <h2 style={{ margin: 0 }}>{editorTitle}</h2>
              <button
                type="button"
                className="master-detail__panel-close"
                onClick={() => setEditorOpen(false)}
                aria-label={t("common.close")}
              >
                &times;
              </button>
            </div>
            <div className="master-detail__panel-body">
        <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {editorError && <Alert tone="danger">{editorError}</Alert>}

          <FormField label={t("rules.editor.field.name")} required>
            {(id) => (
              <Input
                id={id}
                value={editor.name}
                onChange={(e) => setEditor((s) => ({ ...s, name: e.target.value }))}
                placeholder={t("rules.editor.placeholder.name")}
              />
            )}
          </FormField>

          <FormField
            label={
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {t("rules.editor.field.who")}
                <Glossary termKey="deviceGroup" />
              </span>
            }
            required
          >
            {(id) => (
              <>
                <select
                  id={id}
                  className="input"
                  value={editor.deviceGroup}
                  onChange={(e) => setEditor((s) => ({ ...s, deviceGroup: e.target.value }))}
                >
                  <option value="">{t("rules.editor.placeholder.who")}</option>
                  {deviceGroups.data?.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} ({g.members_count})
                    </option>
                  ))}
                </select>
                <p className="form-field__hint" style={{ marginTop: 6 }}>
                  {t("rules.editor.hint.who")}
                </p>
                {customDeviceGroupCount === 0 && deviceGroups.data && (
                  <p className="form-field__hint" style={{ marginTop: 4 }}>
                    {t("rules.editor.onlyDefaultDeviceGroup")}
                  </p>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    marginTop: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setCreateGroupOpen(true)}
                  >
                    {t("rules.editor.createDeviceGroup")}
                  </Button>
                  <Link
                    to="/policies/groups?kind=device"
                    style={{ fontSize: 13, color: "var(--accent, #2563eb)" }}
                  >
                    {t("rules.editor.manageDeviceGroups")}
                  </Link>
                </div>
              </>
            )}
          </FormField>

          <FormField
            label={
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {t("rules.editor.field.what")}
                <Glossary termKey="destinationGroup" />
              </span>
            }
            hint={t("rules.editor.hint.what")}
          >
            {() => <DomainGroupPicker value={editor.urlGroupIds} onChange={(ids) => setEditor((state) => ({ ...state, urlGroupIds: ids }))} />}
          </FormField>

          <FormField
            label={t("rules.editor.field.allowedApps")}
            hint={t("rules.editor.hint.allowedApps")}
          >
            {() => (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: 10,
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {(appGroups.data ?? []).length === 0 && (
                  <span className="app-pkg">
                    {t("rules.editor.noAppGroups")}{" "}
                    <Link
                      to="/policies/groups?kind=app"
                      style={{ color: "var(--accent, #2563eb)" }}
                    >
                      {t("rules.editor.manageAppGroups")}
                    </Link>
                  </span>
                )}
                {appGroups.data?.map((g) => (
                  <label
                    key={g.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={editor.allowedAppGroupIds.includes(g.id)}
                      onChange={() => toggleAppGroupSelection(g.id)}
                    />
                    <span style={{ flex: 1 }}>{g.name}</span>
                    <span className="app-pkg">{g.members_count}</span>
                  </label>
                ))}
              </div>
            )}
          </FormField>

          <FormField label={t("rules.editor.field.action")} required>
            {() => (
              <RadioCardGroup<GroupPolicyDefaultAction>
                name="rule-default-action"
                value={editor.defaultAction}
                onChange={(v) => setEditor((s) => ({ ...s, defaultAction: v }))}
                options={[
                  {
                    value: "allow",
                    title: t("rules.action.blockListed"),
                    desc: t("rules.editor.actionDesc.blockListed"),
                  },
                  {
                    value: "block",
                    title: t("rules.action.allowListed"),
                    desc: t("rules.editor.actionDesc.allowListed"),
                  },
                ]}
              />
            )}
          </FormField>

          <FormField label={t("rules.editor.field.priority")} hint={t("rules.editor.hint.priority")}>
            {(id) => (
              <Input
                id={id}
                type="number"
                value={editor.priority}
                onChange={(e) =>
                  setEditor((s) => ({ ...s, priority: Number(e.target.value) || 100 }))
                }
                min={1}
              />
            )}
          </FormField>

          <FormField
            label={t("rules.editor.field.activation")}
            hint={t(`rules.editor.activationHint.${editor.activation}`)}
          >
            {(id) => (
              <select
                id={id}
                className="input"
                value={editor.activation}
                onChange={(e) => {
                  const next = e.target.value as PolicyActivation;
                  setEditor((s) => ({
                    ...s,
                    activation: next,
                    scheduleProfileId:
                      next === "always" ? "" : s.scheduleProfileId,
                  }));
                }}
              >
                <option value="always">{t("policyActivation.always")}</option>
                <option value="schedule">{t("policyActivation.schedule")}</option>
                <option value="presence">{t("policyActivation.presence")}</option>
              </select>
            )}
          </FormField>

          {(editor.activation === "schedule" || editor.activation === "presence") && (
            <FormField
              label={
                editor.activation === "schedule"
                  ? t("rules.editor.field.scheduleProfile")
                  : t("rules.editor.field.scheduleProfileOptional")
              }
              required={editor.activation === "schedule"}
              hint={
                editor.activation === "schedule"
                  ? t("rules.editor.scheduleProfileHint")
                  : t("rules.editor.scheduleProfileHintPresence")
              }
            >
              {() => <PolicySchedulePicker value={editor.scheduleProfileId} onChange={(id) => setEditor((state) => ({ ...state, scheduleProfileId: id }))} />}
            </FormField>
          )}

          <div style={{ gridColumn: "1 / -1" }}>
            <Switch
              checked={editor.notificationsEnabled}
              onChange={(v) => setEditor((s) => ({ ...s, notificationsEnabled: v }))}
              label={t("rules.notifications.enabled")}
            />
          </div>
          {editor.notificationsEnabled && (
            <>
              <FormField label={t("rules.notifications.activeTitle")}>
                {(id) => <Input id={id} value={editor.activeNotificationTitle} onChange={(e) => setEditor((s) => ({ ...s, activeNotificationTitle: e.target.value }))} />}
              </FormField>
              <FormField label={t("rules.notifications.activeBody")}>
                {(id) => <Input id={id} value={editor.activeNotificationBody} onChange={(e) => setEditor((s) => ({ ...s, activeNotificationBody: e.target.value }))} />}
              </FormField>
              <FormField label={t("rules.notifications.inactiveTitle")}>
                {(id) => <Input id={id} value={editor.inactiveNotificationTitle} onChange={(e) => setEditor((s) => ({ ...s, inactiveNotificationTitle: e.target.value }))} />}
              </FormField>
              <FormField label={t("rules.notifications.inactiveBody")} hint={t("rules.notifications.hint")}>
                {(id) => <Input id={id} value={editor.inactiveNotificationBody} onChange={(e) => setEditor((s) => ({ ...s, inactiveNotificationBody: e.target.value }))} />}
              </FormField>
            </>
          )}

          <FormField
            label={t("rules.editor.field.unknownDomain")}
            hint={t("rules.editor.unknownDomainHint")}
          >
            {(id) => (
              <select
                id={id}
                className="input"
                value={editor.unknownDomainAction}
                onChange={(e) =>
                  setEditor((s) => ({
                    ...s,
                    unknownDomainAction: e.target.value as UnknownDomainAction,
                  }))
                }
              >
                <option value="log">{t("unknownDomainAction.log")}</option>
                <option value="block">{t("unknownDomainAction.block")}</option>
                <option value="request_review">
                  {t("unknownDomainAction.request_review")}
                </option>
              </select>
            )}
          </FormField>

          <FormField label={t("rules.validity.field")} hint={t("rules.validity.hint")}>
            {() => (
              <div style={{ display: "flex", gap: 8 }}>
                <DateTimeField
                  value={editor.validFrom}
                  onChange={(v) => setEditor((s) => ({ ...s, validFrom: v }))}
                  ariaLabel={t("rules.validity.from")}
                />
                <DateTimeField
                  value={editor.validUntil}
                  onChange={(v) => setEditor((s) => ({ ...s, validUntil: v }))}
                  ariaLabel={t("rules.validity.until")}
                />
              </div>
            )}
          </FormField>

          <Switch
            checked={editor.isActive}
            onChange={(v) => setEditor((s) => ({ ...s, isActive: v }))}
            label={t("common.active")}
          />

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button type="button" variant="ghost" onClick={() => setEditorOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={create.isPending || editMut.isPending}>
              {submitLabel}
            </Button>
          </div>
        </form>
            </div>
          </>
        ) : null}
      />

      <Modal
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        title={t("network.deviceGroups.create.title")}
      >
        <form
          onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (!newGroupName.trim()) return;
            createDeviceGroup.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          {createDeviceGroup.error && (
            <Alert tone="danger">{translateError(createDeviceGroup.error)}</Alert>
          )}
          <FormField label={t("network.deviceGroups.create.field.name")} required>
            {(id) => (
              <Input
                id={id}
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                required
              />
            )}
          </FormField>
          <FormField label={t("network.deviceGroups.create.field.description")}>
            {(id) => (
              <Input
                id={id}
                type="text"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
              />
            )}
          </FormField>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button type="button" variant="ghost" onClick={() => setCreateGroupOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={createDeviceGroup.isPending}>
              {t("network.deviceGroups.create.submit")}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={
          pendingDelete
            ? formatMessage("rules.deleteConfirmTitle", { name: pendingDelete.name })
            : ""
        }
        body={
          <>
            <p style={{ margin: "0 0 8px 0" }}>{t("rules.deleteConfirmBody")}</p>
            {pendingDelete && (() => {
              const siblings = (list.data ?? []).filter(
                (r) =>
                  r.device_group === pendingDelete.device_group &&
                  r.id !== pendingDelete.id &&
                  r.is_active,
              );
              if (siblings.length === 0 && pendingDelete.is_active) {
                return (
                  <Alert tone="warning">
                    {formatMessage("rules.deleteLastRuleWarning", {
                      group: pendingDelete.device_group_name,
                    })}
                  </Alert>
                );
              }
              return null;
            })()}
          </>
        }
        tone="danger"
        loading={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
        }}
      />

      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail ? detail.name : ""}
      >
        {detail && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="kv-grid">
              <div className="kv">
                <div className="kv__label">{t("rules.column.who")}</div>
                <div className="kv__value">{detail.device_group_name}</div>
                <div className="app-pkg" style={{ marginTop: 2 }}>
                  {detail.device_count} {t("rules.devicesLabel")}
                </div>
              </div>
              <div className="kv">
                <div className="kv__label">{t("rules.column.action")}</div>
                <div className="kv__value">
                  {detail.default_action === "allow" ? (
                    <span className="badge badge--danger">
                      {t("rules.action.blockListed")}
                    </span>
                  ) : (
                    <span className="badge badge--success">
                      {t("rules.action.allowListed")}
                    </span>
                  )}
                </div>
              </div>
              <div className="kv">
                <div className="kv__label">{t("rules.column.priority")}</div>
                <div className="kv__value num">{detail.priority}</div>
              </div>
              <div className="kv">
                <div className="kv__label">{t("rules.column.status")}</div>
                <div className="kv__value">
                  {detail.is_active ? t("common.active") : t("common.inactive")}
                </div>
              </div>
              <div className="kv">
                <div className="kv__label">{t("rules.column.activation")}</div>
                <div className="kv__value">
                  {t(`policyActivation.${detail.activation}`)}
                  {detail.schedule_profile_name && (
                    <span className="app-pkg" style={{ marginLeft: 4 }}>
                      · {detail.schedule_profile_name}
                    </span>
                  )}
                </div>
              </div>
              <div className="kv">
                <div className="kv__label">{t("rules.column.unknownDomain")}</div>
                <div className="kv__value">
                  {t(`unknownDomainAction.${detail.unknown_domain_action}`)}
                </div>
              </div>
            </div>

            <div>
              <div className="kv__label" style={{ marginBottom: 8 }}>
                {t("rules.column.what")}
              </div>
              {detail.url_group_names && detail.url_group_names.length > 0 ? (
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {detail.url_group_names.map((n) => (
                    <li key={n} style={{ fontSize: 13.5 }}>
                      {n}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="app-pkg">{t("rules.column.whatEmpty")}</span>
              )}
            </div>

            <div>
              <div className="kv__label" style={{ marginBottom: 8 }}>
                {t("rules.column.allowedApps")}
              </div>
              {(detail.allowed_app_group_names ?? []).length > 0 ? (
                <>
                  <ul style={{ paddingLeft: 18, margin: 0 }}>
                    {detail.allowed_app_group_names.map((n) => (
                      <li key={n} style={{ fontSize: 13.5 }}>
                        {n}
                      </li>
                    ))}
                  </ul>
                  <div className="app-pkg" style={{ marginTop: 4 }}>
                    {detail.allowed_apps_count} {t("rules.allowedAppsLabel")}
                  </div>
                </>
              ) : (
                <span className="app-pkg">—</span>
              )}
            </div>

            <Alert tone="info">{t("rules.detail.precedenceHint")}</Alert>
          </div>
        )}
      </Modal>
    </>
  );
}
